import { renderTemplateContent } from "../contentRenderer";
import { mergeTemplateDesign, templateDesignToCssVars } from "../design";
import { DefaultTemplateLink } from "../templateLink";
import { WebBuilderPageRegion } from "../WebBuilderPageRegion";
import { WebGlobalBuilderRegion } from "../WebGlobalBuilderRegion";
import { webSystemTemplateDesignDefaults } from "../webSystemTemplateDesign";
import { DentalMobileMenu } from "./DentalMobileMenu";
import type {
    ResolvedTemplateDesign,
    SiteTemplateDefinition,
    TemplateLinkComponent,
    TemplateRenderProps,
} from "../types";

function DentalStyles() {
    return (
        <style>{`
            [data-web-template="dental-studio"] {
                --dental-hard-shadow: 7px 7px 0 color-mix(in srgb, var(--site-primary) 13%, transparent);
                background: var(--site-background);
                color: var(--site-text);
                isolation: isolate;
                scroll-behavior: smooth;
                scroll-padding-top: 86px;
            }

            [data-web-template="dental-studio"] ::selection {
                background: var(--site-accent);
                color: var(--site-primary);
            }

            [data-web-template="dental-studio"] :focus-visible {
                outline: 3px solid var(--site-accent);
                outline-offset: 4px;
            }

            [data-web-template="dental-studio"] *,
            [data-web-template="dental-studio"] *::before,
            [data-web-template="dental-studio"] *::after {
                box-sizing: border-box;
            }

            [data-web-template="dental-studio"] .site-builder-section,
            [data-web-template="dental-studio"] [data-site-builder-block="footer"] {
                padding-inline: clamp(1.25rem, 2.4vw, 2.5rem);
                position: relative;
            }

            [data-web-template="dental-studio"] .site-builder-grid {
                display: grid;
                gap: clamp(1.35rem, 3vw, 2.75rem);
                margin-inline: auto;
                width: min(100%, var(--site-container));
            }

            [data-web-template="dental-studio"] .site-builder-grid:not(.site-builder-grid-placed) {
                grid-template-columns: repeat(12, minmax(0, 1fr));
            }

            [data-web-template="dental-studio"] .site-builder-grid:not(.site-builder-grid-placed) > .site-builder-grid-item {
                grid-column: 1 / -1;
            }

            [data-web-template="dental-studio"] .site-builder-column {
                align-content: start;
                display: grid;
                gap: 1.1rem;
                min-width: 0;
            }

            [data-web-template="dental-studio"] .site-builder-column-inline {
                align-items: center;
                display: flex;
                flex-wrap: wrap;
            }

            [data-web-template="dental-studio"] :where(
                .site-builder-text-h1,
                .site-builder-text-h2,
                .site-builder-text-h3
            ) {
                font-stretch: condensed;
                text-wrap: balance;
            }

            [data-web-template="dental-studio"] .site-builder-text-h1 {
                line-height: .82 !important;
            }

            [data-web-template="dental-studio"] .site-builder-text-h2 {
                line-height: .9 !important;
            }

            [data-web-template="dental-studio"] .site-builder-button {
                align-items: center;
                letter-spacing: .105em;
                min-height: 54px;
                text-transform: uppercase;
                transition: background-color 180ms ease, border-color 180ms ease, box-shadow 220ms ease, color 180ms ease, transform 220ms cubic-bezier(.2,.75,.25,1);
            }

            [data-web-template="dental-studio"] .site-builder-button:hover,
            [data-web-template="dental-studio"] .site-builder-button:focus-visible {
                box-shadow: 6px 6px 0 color-mix(in srgb, var(--site-accent) 72%, transparent);
                transform: translateX(4px);
            }

            [data-web-template="dental-studio"] .site-builder-card {
                box-shadow: var(--dental-hard-shadow);
                transition: border-color 220ms ease, box-shadow 260ms ease, transform 260ms cubic-bezier(.2,.75,.25,1);
            }

            [data-web-template="dental-studio"] .site-builder-card:hover,
            [data-web-template="dental-studio"] .site-builder-card:focus-within {
                border-color: var(--site-primary) !important;
                box-shadow: 10px 10px 0 color-mix(in srgb, var(--site-accent) 58%, transparent);
                transform: translate(-2px, -2px);
            }

            [data-web-template="dental-studio"] .site-builder-navigation {
                align-items: center;
                display: flex;
                flex-wrap: wrap;
                gap: 0;
                justify-content: center;
            }

            [data-web-template="dental-studio"] .site-builder-navigation-item {
                display: inline-flex;
            }

            [data-web-template="dental-studio"] .site-builder-navigation-link,
            [data-web-template="dental-studio"] [data-document-role="headerNavigation"] .site-builder-text {
                display: inline-flex;
                position: relative;
                transition: background-color 180ms ease, color 180ms ease;
                width: auto !important;
            }

            [data-web-template="dental-studio"] [data-document-role="headerNavigation"] > [data-site-builder-block="text"] {
                border-left: 1px solid var(--site-border);
            }

            [data-web-template="dental-studio"] [data-document-role="headerNavigation"] > [data-site-builder-block="text"]:last-child {
                border-right: 1px solid var(--site-border);
            }

            [data-web-template="dental-studio"] [data-document-role="headerNavigation"] .site-builder-text {
                min-height: 48px;
                padding: 1rem clamp(.65rem,1.15vw,1.1rem);
            }

            [data-web-template="dental-studio"] .site-builder-navigation-link:hover,
            [data-web-template="dental-studio"] [data-document-role="headerNavigation"] .site-builder-text:hover {
                background: var(--site-accent);
                color: var(--site-primary) !important;
            }

            [data-web-template="dental-studio"] [data-web-region="header"] {
                left: 0;
                padding-inline: clamp(1rem,2.4vw,2.5rem);
                position: sticky;
                right: 0;
                top: 0;
                transition: background-color 220ms ease, box-shadow 220ms ease, min-height 220ms ease, padding 220ms ease;
                z-index: 70;
            }

            [data-web-template="dental-studio"] [data-web-region="header"] > .site-builder-grid {
                align-items: center;
                gap: clamp(.75rem,1.4vw,1.5rem);
            }

            [data-web-template="dental-studio"] [data-web-region="header"][data-dental-header-scrolled] {
                background: color-mix(in srgb, var(--site-surface) 97%, transparent) !important;
                box-shadow: 0 12px 36px rgb(7 24 36 / .16);
                min-height: 58px !important;
            }

            [data-web-template="dental-studio"] [data-document-role="brand"] {
                clip-path: polygon(0 0, calc(100% - 24px) 0, 100% 50%, calc(100% - 24px) 100%, 0 100%);
                margin-left: clamp(-2.5rem,-2.4vw,-1rem);
                transition: min-height 220ms ease;
            }

            [data-web-template="dental-studio"] [data-web-region="header"][data-dental-header-scrolled] [data-document-role="brand"] {
                min-height: 58px !important;
            }

            [data-web-template="dental-studio"] [data-document-role="headerNavigation"] {
                gap: 0 !important;
            }

            [data-web-template="dental-studio"] [data-document-role="headerActions"] {
                flex-wrap: nowrap;
                justify-content: flex-end;
            }

            [data-web-template="dental-studio"] [data-document-role="headerCta"],
            [data-web-template="dental-studio"] [data-document-role="localeActions"] {
                flex: 0 0 auto;
            }

            [data-web-template="dental-studio"] [data-web-region="hero"] {
                border-bottom: 1px solid var(--site-primary);
                overflow: hidden;
                padding-inline: 0;
            }

            [data-web-template="dental-studio"] [data-web-region="hero"] > .site-builder-grid {
                gap: 0 !important;
                grid-auto-rows: minmax(0, auto) !important;
                grid-template-columns: minmax(0,.94fr) minmax(520px,1.06fr) !important;
                max-width: none;
                width: 100%;
            }

            [data-web-template="dental-studio"] [data-web-region="hero"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="heroCopy"]) {
                grid-column: 1 !important;
                grid-row: 1 !important;
            }

            [data-web-template="dental-studio"] [data-web-region="hero"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="heroVisual"]) {
                grid-column: 2 !important;
                grid-row: 1 !important;
            }

            [data-web-template="dental-studio"] [data-document-role="heroCopy"] {
                background-image: linear-gradient(90deg,color-mix(in srgb,var(--site-primary) 7%,transparent) 1px,transparent 1px);
                background-size: 64px 100%;
                padding: clamp(4.5rem,8vw,8rem) clamp(2rem,4vw,5.5rem) clamp(4.5rem,8vw,8rem) max(1.25rem,calc((100cqw - var(--site-container)) / 2)) !important;
            }

            [data-web-template="dental-studio"] [data-document-role="heroCopy"]:has(> [data-document-role="heroHeading"]) {
                align-content: center;
                column-gap: .8rem;
                display: grid;
                grid-template-columns: repeat(12,minmax(0,1fr));
                row-gap: 1.35rem;
                width: 100%;
            }

            [data-web-template="dental-studio"] [data-document-role="heroEyebrow"],
            [data-web-template="dental-studio"] [data-document-role="heroHeading"],
            [data-web-template="dental-studio"] [data-document-role="heroDescription"],
            [data-web-template="dental-studio"] [data-document-role="heroProof"] {
                grid-column: 1 / -1;
            }

            [data-web-template="dental-studio"] [data-document-role="heroPrimaryAction"] {
                grid-column: 1 / span 6;
            }

            [data-web-template="dental-studio"] [data-document-role="heroSecondaryAction"] {
                grid-column: 7 / span 6;
            }

            [data-web-template="dental-studio"] [data-document-role="heroHeading"] {
                font-size: clamp(54px,5.6vw,100px) !important;
            }

            [data-web-template="dental-studio"] [data-document-role="heroProof"] {
                border-left: 4px solid var(--site-accent);
                margin-top: .5rem;
                padding-left: .85rem;
                text-transform: uppercase;
            }

            [data-web-template="dental-studio"] [data-document-role="heroVisual"],
            [data-web-template="dental-studio"] [data-document-role="editorialVisual"] {
                isolation: isolate;
                overflow: hidden;
                position: relative;
            }

            [data-web-template="dental-studio"] [data-document-role="heroVisual"]::after {
                background: var(--site-accent);
                bottom: -1px;
                clip-path: polygon(0 55%,100% 0,72% 100%,0 100%);
                content: "";
                height: min(25cqw,320px);
                left: -1px;
                mix-blend-mode: multiply;
                pointer-events: none;
                position: absolute;
                width: min(26cqw,340px);
                z-index: 1;
            }

            [data-web-template="dental-studio"] [data-document-role="heroVisual"] .site-builder-image,
            [data-web-template="dental-studio"] [data-document-role="editorialVisual"] .site-builder-image {
                filter: saturate(.72) contrast(1.06) brightness(.98);
                height: 100% !important;
                min-height: 100%;
                object-fit: cover;
                transition: filter 600ms cubic-bezier(.2,.75,.25,1), transform 800ms cubic-bezier(.2,.75,.25,1);
                width: 100% !important;
            }

            [data-web-template="dental-studio"] [data-document-role="heroVisual"]:hover .site-builder-image,
            [data-web-template="dental-studio"] [data-document-role="editorialVisual"]:hover .site-builder-image {
                filter: saturate(.96) contrast(1.03) brightness(1);
                transform: scale(1.025);
            }

            [data-web-template="dental-studio"] [data-document-role="editorialVisual"] {
                clip-path: polygon(0 0,100% 0,100% calc(100% - 46px),calc(100% - 46px) 100%,0 100%);
            }

            [data-web-template="dental-studio"] [data-document-role="heroFloatingProof"] {
                bottom: clamp(1rem,2.4vw,2rem);
                left: auto;
                position: absolute;
                right: clamp(1rem,2.4vw,2rem);
                z-index: 3;
            }

            [data-web-template="dental-studio"] [data-web-region="credentials"] {
                padding-inline: clamp(1.25rem,2.4vw,2.5rem);
            }

            [data-web-template="dental-studio"] [data-web-region="credentials"] > .site-builder-grid {
                gap: 0 !important;
            }

            [data-web-template="dental-studio"] [data-web-region="credentials"] > .site-builder-grid > .site-builder-grid-item {
                border-left: 1px solid var(--site-primary);
                padding-inline: clamp(1rem,2.4vw,2.25rem);
            }

            [data-web-template="dental-studio"] [data-web-region="credentials"] > .site-builder-grid > .site-builder-grid-item:last-child {
                border-right: 1px solid var(--site-primary);
            }

            [data-web-template="dental-studio"] [data-web-region="credentials"] .site-builder-text-h3 {
                text-transform: uppercase;
            }

            [data-web-template="dental-studio"] [data-web-region$="Hero"] {
                background: var(--site-primary) !important;
                border-bottom: 6px solid var(--site-accent);
                overflow: hidden;
            }

            [data-web-template="dental-studio"] [data-web-region$="Hero"]::after {
                background: var(--site-accent);
                content: "";
                height: clamp(80px,12vw,180px);
                opacity: .12;
                pointer-events: none;
                position: absolute;
                right: -4vw;
                top: -3vw;
                transform: rotate(24deg);
                width: clamp(180px,24vw,380px);
            }

            [data-web-template="dental-studio"] [data-web-region$="Hero"] :where(.site-builder-text-h1,.site-builder-text-h2,.site-builder-text-h3) {
                color: var(--site-surface) !important;
            }

            [data-web-template="dental-studio"] [data-web-region$="Hero"] p.site-builder-text-body {
                color: color-mix(in srgb,var(--site-surface) 70%,var(--site-primary)) !important;
            }

            [data-web-template="dental-studio"] [data-web-region$="Hero"] .site-builder-badge {
                border-color: var(--site-accent) !important;
                color: var(--site-accent) !important;
            }

            [data-web-template="dental-studio"] [data-web-region$="Hero"] .site-builder-button {
                background: var(--site-accent) !important;
                border-color: var(--site-accent) !important;
                color: var(--site-primary) !important;
            }

            [data-web-template="dental-studio"] :where(
                [data-web-region="treatments"],
                [data-web-region="treatmentGuide"],
                [data-web-region="teamProfiles"],
                [data-web-region="visitSteps"],
                [data-web-region="patientStories"]
            ) .site-builder-card {
                border-top: 5px solid var(--site-primary) !important;
            }

            [data-web-template="dental-studio"] :where(
                [data-web-region="treatments"],
                [data-web-region="treatmentGuide"],
                [data-web-region="teamProfiles"],
                [data-web-region="visitSteps"],
                [data-web-region="patientStories"]
            ) .site-builder-card:hover {
                border-top-color: var(--site-accent) !important;
            }

            [data-web-template="dental-studio"] [data-document-role="comfortCard"] .site-builder-card {
                border-left: 6px solid var(--site-accent) !important;
                box-shadow: 10px 10px 0 color-mix(in srgb,var(--site-accent) 28%,transparent);
            }

            [data-web-template="dental-studio"] [data-web-region="clinicValues"] .site-builder-separator {
                border-color: var(--site-primary) !important;
            }

            [data-web-template="dental-studio"] [data-site-builder-block="accordion"] {
                border-top: 2px solid var(--site-primary);
            }

            [data-web-template="dental-studio"] [data-site-builder-block="accordionItem"] {
                border-bottom: 1px solid var(--site-border);
            }

            [data-web-template="dental-studio"] .site-builder-form {
                box-shadow: 9px 9px 0 color-mix(in srgb,var(--site-primary) 15%,transparent);
            }

            [data-web-template="dental-studio"] .site-builder-form :where(input,select,textarea) {
                border-radius: 0 !important;
                min-height: 50px;
                transition: border-color 180ms ease, box-shadow 180ms ease;
            }

            [data-web-template="dental-studio"] .site-builder-form :where(input,select,textarea):focus {
                border-color: var(--site-primary) !important;
                box-shadow: 4px 4px 0 color-mix(in srgb,var(--site-accent) 62%,transparent);
            }

            [data-web-template="dental-studio"] [data-site-builder-block="map"] {
                filter: saturate(.58) contrast(1.04);
            }

            [data-web-template="dental-studio"] :where(
                [data-web-region="treatmentBooking"],
                [data-web-region="teamBooking"],
                [data-web-region="visitBooking"],
                [data-web-region="urgentContact"]
            ) {
                border-block: 1px solid var(--site-primary);
            }

            [data-web-template="dental-studio"] :where(
                [data-web-region="treatmentBooking"],
                [data-web-region="teamBooking"],
                [data-web-region="visitBooking"],
                [data-web-region="urgentContact"]
            ) .site-builder-button {
                width: 100% !important;
            }

            [data-web-template="dental-studio"] [data-web-region="footer"] {
                border-top: 7px solid var(--site-accent) !important;
            }

            [data-web-template="dental-studio"] [data-web-region="footer"] .site-builder-navigation {
                align-items: flex-start;
                flex-direction: column;
                gap: .7rem;
            }

            [data-web-template="dental-studio"] [data-web-region="footer"] a {
                transition: color 180ms ease, transform 180ms ease;
            }

            [data-web-template="dental-studio"] [data-web-region="footer"] a:hover {
                color: var(--site-accent) !important;
                transform: translateX(3px);
            }

            [data-web-template="dental-studio"]:not([data-web-template-editor="true"])[data-dental-runtime-ready] [data-dental-reveal]:not([data-dental-revealed]) {
                opacity: 0;
                transform: translateY(28px);
            }

            [data-web-template="dental-studio"]:not([data-web-template-editor="true"])[data-dental-runtime-ready] [data-dental-reveal] {
                opacity: 1;
                transform: translateY(0);
                transition: opacity 700ms cubic-bezier(.2,.75,.25,1), transform 700ms cubic-bezier(.2,.75,.25,1);
                will-change: opacity, transform;
            }

            [data-web-template="dental-studio"][data-web-template-editor="true"],
            [data-web-template="dental-studio"][data-web-template-editor="true"] * {
                scroll-behavior: auto !important;
            }

            [data-web-template="dental-studio"][data-web-template-editor="true"] *,
            [data-web-template="dental-studio"][data-web-template-editor="true"] *::before,
            [data-web-template="dental-studio"][data-web-template-editor="true"] *::after {
                animation: none !important;
                transition: none !important;
            }

            [data-web-template="dental-studio"] .dental-mobile-menu-toggle {
                display: none;
            }

            @container (max-width: 900px) {
                [data-web-template="dental-studio"] .site-builder-grid[data-site-builder-stack-on-mobile="true"] {
                    grid-auto-rows: auto !important;
                    grid-template-columns: minmax(0,1fr) !important;
                    grid-template-rows: none !important;
                }

                [data-web-template="dental-studio"] .site-builder-grid[data-site-builder-stack-on-mobile="true"] > .site-builder-grid-item {
                    grid-column: 1 !important;
                    grid-row: auto !important;
                }

                [data-web-template="dental-studio"] .site-builder-section:not([data-web-region="header"]):not([data-web-region="hero"]) {
                    padding-bottom: clamp(4.25rem,11vw,6rem) !important;
                    padding-top: clamp(4.25rem,11vw,6rem) !important;
                }

                [data-web-template="dental-studio"] [data-web-region="credentials"] > .site-builder-grid {
                    grid-template-columns: repeat(2,minmax(0,1fr)) !important;
                }

                [data-web-template="dental-studio"] [data-web-region="credentials"] > .site-builder-grid > .site-builder-grid-item {
                    border-bottom: 1px solid var(--site-primary);
                    border-left: 1px solid var(--site-primary);
                    grid-column: auto !important;
                    grid-row: auto !important;
                    padding-block: 1rem;
                }

                [data-web-template="dental-studio"] [data-web-region="credentials"] > .site-builder-grid > .site-builder-grid-item:nth-child(even) {
                    border-right: 1px solid var(--site-primary);
                }

                [data-web-template="dental-studio"] [data-web-region="header"] {
                    min-height: 66px !important;
                    padding-inline: 1rem;
                }

                [data-web-template="dental-studio"] [data-web-region="header"] > .site-builder-grid {
                    grid-template-columns: minmax(0,1fr) auto !important;
                }

                [data-web-template="dental-studio"] [data-web-region="header"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="brand"]) {
                    display: block;
                    grid-column: 1 !important;
                    grid-row: 1 !important;
                }

                [data-web-template="dental-studio"] [data-document-role="brand"] {
                    margin-left: -1rem;
                    max-width: 250px;
                    min-height: 66px !important;
                }

                [data-web-template="dental-studio"] [data-web-region="header"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerNavigation"]),
                [data-web-template="dental-studio"] [data-web-region="header"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerActions"]) {
                    display: none;
                }

                [data-web-template="dental-studio"] [data-web-region="header"] .dental-mobile-menu-toggle {
                    align-items: center;
                    background: var(--site-accent);
                    border: 1px solid var(--site-primary);
                    color: var(--site-primary);
                    cursor: pointer;
                    display: grid;
                    gap: 5px;
                    height: 44px;
                    padding: 10px;
                    place-content: center;
                    position: absolute;
                    right: 1rem;
                    top: 11px;
                    width: 44px;
                    z-index: 4;
                }

                [data-web-template="dental-studio"] .dental-mobile-menu-toggle > span {
                    background: currentColor;
                    display: block;
                    height: 2px;
                    transition: opacity 180ms ease, transform 180ms ease;
                    width: 23px;
                }

                [data-web-template="dental-studio"] [data-web-region="header"][data-dental-mobile-menu-open] {
                    background: var(--site-surface) !important;
                    bottom: 0;
                    left: 0;
                    min-height: 100dvh !important;
                    overflow-y: auto;
                    position: fixed;
                    right: 0;
                    top: 0;
                    z-index: 90;
                }

                [data-web-template="dental-studio"] [data-web-region="header"][data-dental-mobile-menu-open] > .site-builder-grid {
                    align-content: start;
                    min-height: 100%;
                    padding-bottom: 2rem;
                }

                [data-web-template="dental-studio"] [data-web-region="header"][data-dental-mobile-menu-open] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerNavigation"]),
                [data-web-template="dental-studio"] [data-web-region="header"][data-dental-mobile-menu-open] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerActions"]) {
                    display: block;
                    grid-column: 1 / -1 !important;
                }

                [data-web-template="dental-studio"] [data-web-region="header"][data-dental-mobile-menu-open] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerNavigation"]) {
                    grid-row: 2 !important;
                    margin-top: clamp(3rem,9vh,5rem);
                }

                [data-web-template="dental-studio"] [data-web-region="header"][data-dental-mobile-menu-open] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerActions"]) {
                    grid-row: 3 !important;
                }

                [data-web-template="dental-studio"] [data-web-region="header"][data-dental-mobile-menu-open] [data-document-role="headerNavigation"],
                [data-web-template="dental-studio"] [data-web-region="header"][data-dental-mobile-menu-open] [data-document-role="headerActions"] {
                    align-items: stretch;
                    flex-direction: column;
                    gap: 0 !important;
                }

                [data-web-template="dental-studio"] [data-web-region="header"][data-dental-mobile-menu-open] [data-document-role="headerNavigation"] > [data-site-builder-block="text"] {
                    border-bottom: 1px solid var(--site-border);
                    border-left: 0;
                    border-right: 0;
                    padding-block: 0;
                    width: 100%;
                }

                [data-web-template="dental-studio"] [data-web-region="header"][data-dental-mobile-menu-open] [data-document-role="headerNavigation"] .site-builder-text {
                    color: var(--site-text) !important;
                    display: block;
                    font-family: var(--site-heading-font) !important;
                    font-size: clamp(2rem,9vw,3.15rem) !important;
                    font-weight: 900 !important;
                    letter-spacing: -.025em !important;
                    line-height: 1 !important;
                    min-height: 58px;
                    padding: .9rem 0;
                    text-transform: uppercase;
                    width: 100% !important;
                }

                [data-web-template="dental-studio"] [data-web-region="header"][data-dental-mobile-menu-open] [data-document-role="headerActions"] {
                    gap: .75rem !important;
                    margin-top: 1rem;
                }

                [data-web-template="dental-studio"] [data-web-region="header"][data-dental-mobile-menu-open] [data-document-role="headerActions"] > *,
                [data-web-template="dental-studio"] [data-web-region="header"][data-dental-mobile-menu-open] [data-document-role="headerCta"] {
                    width: 100% !important;
                }

                [data-web-template="dental-studio"] [data-web-region="header"][data-dental-mobile-menu-open] .dental-mobile-menu-toggle > span:first-child {
                    transform: translateY(7px) rotate(45deg);
                }

                [data-web-template="dental-studio"] [data-web-region="header"][data-dental-mobile-menu-open] .dental-mobile-menu-toggle > span:nth-child(2) {
                    opacity: 0;
                }

                [data-web-template="dental-studio"] [data-web-region="header"][data-dental-mobile-menu-open] .dental-mobile-menu-toggle > span:last-child {
                    transform: translateY(-7px) rotate(-45deg);
                }

                [data-web-template="dental-studio"] [data-web-region="hero"] > .site-builder-grid {
                    grid-template-columns: minmax(0,1fr) !important;
                }

                [data-web-template="dental-studio"] [data-web-region="hero"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="heroCopy"]),
                [data-web-template="dental-studio"] [data-web-region="hero"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="heroVisual"]) {
                    grid-column: 1 !important;
                    grid-row: auto !important;
                }

                [data-web-template="dental-studio"] [data-document-role="heroCopy"] {
                    padding: clamp(4rem,10vw,6rem) 1.25rem !important;
                }

                [data-web-template="dental-studio"] [data-document-role="heroCopy"]:has(> [data-document-role="heroHeading"]) {
                    grid-template-columns: repeat(12,minmax(0,1fr));
                }

                [data-web-template="dental-studio"] [data-document-role="heroVisual"] {
                    min-height: 540px !important;
                }

                [data-web-template="dental-studio"] [data-web-region$="Hero"] [data-document-role="editorialVisual"] {
                    min-height: 480px;
                }

                [data-web-template="dental-studio"] .site-builder-form-fields {
                    grid-template-columns: minmax(0,1fr) !important;
                }

                [data-web-template="dental-studio"] .site-builder-form-field {
                    grid-column: 1 !important;
                }

                [data-web-template="dental-studio"] [data-site-builder-block="map"] {
                    min-height: 460px !important;
                }
            }

            @container (max-width: 640px) {
                [data-web-template="dental-studio"] .site-builder-section,
                [data-web-template="dental-studio"] [data-site-builder-block="footer"] {
                    padding-inline: 1rem;
                }

                [data-web-template="dental-studio"] .site-builder-text-h1 {
                    font-size: clamp(3.4rem,17vw,5.6rem) !important;
                }

                [data-web-template="dental-studio"] .site-builder-text-h2 {
                    font-size: clamp(2.7rem,13vw,4.5rem) !important;
                }

                [data-web-template="dental-studio"] [data-document-role="heroCopy"]:has(> [data-document-role="heroHeading"]) {
                    grid-template-columns: minmax(0,1fr);
                }

                [data-web-template="dental-studio"] [data-document-role="heroCopy"] > * {
                    grid-column: 1 !important;
                }

                [data-web-template="dental-studio"] [data-document-role="heroHeading"] {
                    font-size: clamp(3rem,12vw,4.5rem) !important;
                }

                [data-web-template="dental-studio"] [data-document-role="heroPrimaryAction"],
                [data-web-template="dental-studio"] [data-document-role="heroSecondaryAction"] {
                    width: 100% !important;
                }

                [data-web-template="dental-studio"] [data-document-role="heroVisual"] {
                    min-height: 440px !important;
                }

                [data-web-template="dental-studio"] [data-document-role="heroFloatingProof"] {
                    bottom: 1rem;
                    left: 1rem;
                    max-width: none;
                    right: 1rem;
                }

                [data-web-template="dental-studio"] [data-web-region="credentials"] > .site-builder-grid {
                    grid-template-columns: minmax(0,1fr) !important;
                }

                [data-web-template="dental-studio"] [data-web-region="credentials"] > .site-builder-grid > .site-builder-grid-item {
                    border-right: 1px solid var(--site-primary);
                }

                [data-web-template="dental-studio"] [data-document-role="brand"] {
                    max-width: 220px;
                }

                [data-web-template="dental-studio"] [data-site-builder-block="map"] {
                    min-height: 400px !important;
                }
            }

            @media (pointer: coarse) {
                [data-web-template="dental-studio"] :where(
                    .site-builder-button,
                    .site-builder-navigation-link,
                    button,
                    input,
                    select,
                    textarea
                ) {
                    min-height: 44px;
                }
            }

            @media (prefers-reduced-motion: reduce) {
                [data-web-template="dental-studio"],
                [data-web-template="dental-studio"] * {
                    scroll-behavior: auto !important;
                }

                [data-web-template="dental-studio"] [data-dental-reveal],
                [data-web-template="dental-studio"] [data-dental-reveal]:not([data-dental-revealed]) {
                    opacity: 1 !important;
                    transform: none !important;
                }

                [data-web-template="dental-studio"] *,
                [data-web-template="dental-studio"] *::before,
                [data-web-template="dental-studio"] *::after {
                    animation-duration: .01ms !important;
                    animation-iteration-count: 1 !important;
                    transition-duration: .01ms !important;
                }

                [data-web-template="dental-studio"] :where(
                    .site-builder-card,
                    .site-builder-button,
                    [data-document-role="heroVisual"],
                    [data-document-role="editorialVisual"]
                ):is(:hover,:focus-within) {
                    transform: none !important;
                }

                [data-web-template="dental-studio"] :where(
                    [data-document-role="heroVisual"],
                    [data-document-role="editorialVisual"]
                ):is(:hover,:focus-within) .site-builder-image {
                    transform: none !important;
                }
            }
        `}</style>
    );
}

function DentalFallbackPage({
    props,
    design,
    LinkComponent,
}: {
    props: TemplateRenderProps;
    design: ResolvedTemplateDesign;
    LinkComponent: TemplateLinkComponent;
}) {
    const page = props.page;

    if (!page) {
        return null;
    }

    return (
        <main
            className="px-5 py-20 sm:px-6"
            data-builder-document-region="page"
        >
            <article className="mx-auto max-w-[920px] border-t-8 border-[var(--site-accent)] bg-[var(--site-surface)] p-6 leading-8 text-[var(--site-muted)] shadow-[9px_9px_0_color-mix(in_srgb,var(--site-primary)_14%,transparent)] sm:p-12 [&_a]:font-bold [&_a]:text-[var(--site-primary)] [&_blockquote]:border-l-4 [&_blockquote]:border-[var(--site-accent)] [&_blockquote]:pl-5 [&_h1]:mb-8 [&_h1]:font-[var(--site-heading-font)] [&_h1]:text-6xl [&_h1]:font-black [&_h1]:uppercase [&_h1]:leading-[.85] [&_h1]:text-[var(--site-text)] [&_h2]:mb-4 [&_h2]:mt-11 [&_h2]:font-[var(--site-heading-font)] [&_h2]:text-4xl [&_h2]:font-black [&_h2]:uppercase [&_h2]:leading-[.9] [&_h2]:text-[var(--site-text)] [&_li]:mb-2 [&_ol]:mb-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_p]:mb-5 [&_strong]:text-[var(--site-text)] [&_ul]:mb-6 [&_ul]:list-disc [&_ul]:pl-6">
                <h1>{page.title}</h1>
                {renderTemplateContent(
                    page,
                    design,
                    LinkComponent,
                    props.leadForm,
                    props.readOnly,
                )}
            </article>
        </main>
    );
}

export const dentalStudioTemplate: SiteTemplateDefinition = {
    manifest: {
        code: "dental-studio",
        name: "Zubní ordinace",
        type: "website",
        category: "healthcare",
        description: "Moderní, klidná a obsahově promyšlená šablona pro zubní ordinace a menší stomatologické kliniky.",
        previewImage: "/templates/dental-studio/preview.jpg",
        defaults: webSystemTemplateDesignDefaults["dental-studio"],
        pageTypes: {
            home: {
                label: "Dental homepage",
                slots: ["richContent"],
            },
            content: {
                label: "Dental content page",
                slots: ["richContent"],
            },
        },
        editable: {
            colors: true,
            typography: true,
            radius: true,
            header: ["builder"],
            footer: ["builder"],
            buttons: ["pill", "solid", "outline"],
        },
    },
    render: (props: TemplateRenderProps) => {
        const design = mergeTemplateDesign(
            dentalStudioTemplate.manifest.defaults,
            props.designOverride ?? props.site?.templateDesign,
        );
        const LinkComponent = props.LinkComponent ?? DefaultTemplateLink;

        return (
            <div
                data-web-template="dental-studio"
                data-web-template-editor={props.editorMode ? "true" : undefined}
                className="min-h-screen overflow-x-hidden bg-[var(--site-background)] font-[var(--site-body-font)] text-[var(--site-text)] antialiased"
                style={{
                    ...templateDesignToCssVars(design),
                    containerType: "inline-size",
                }}
            >
                <DentalStyles />
                <WebGlobalBuilderRegion
                    design={design}
                    LinkComponent={LinkComponent}
                    props={props}
                    region="header"
                >
                    <DentalMobileMenu
                        languageCode={props.page?.languageCode ?? props.site?.defaultLanguageCode}
                    />
                </WebGlobalBuilderRegion>
                <WebBuilderPageRegion
                    design={design}
                    fallback={(
                        <DentalFallbackPage
                            props={props}
                            design={design}
                            LinkComponent={LinkComponent}
                        />
                    )}
                    LinkComponent={LinkComponent}
                    props={props}
                />
                <WebGlobalBuilderRegion
                    design={design}
                    LinkComponent={LinkComponent}
                    props={props}
                    region="footer"
                />
            </div>
        );
    },
};
